/**
 * What a person set on the review screen, kept in `sessionStorage` per
 * Vorgang and reading (plan 4 §5: „F5 verliert nichts“).
 *
 * In `lib/` and not in the schema feature because two screens read it: the
 * review writes it, and the editor opened from the review („Im Editor
 * bearbeiten“) puts the same figures on its walls instead of starting from
 * the bare reading again.
 *
 * Keyed by the reading as well as the Vorgang: a new reading is a new set of
 * walls, and figures typed for the old one must not land on it. The list is
 * ordered — its last entry is what ⌘/Strg+Z takes back.
 */
import type { Resolution } from "@/api";

import { MAX_LENGTH_MM, MIN_LENGTH_MM } from "./length";

const PREFIX = "k60.pruefung";

export function resolutionsKey(caseId: string, extractionId: string): string {
  return `${PREFIX}.${caseId}.${extractionId}`;
}

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    // A browser that refuses storage (privacy mode, a sandboxed frame) still
    // gets a working screen — only F5 forgets.
    return null;
  }
}

function isResolution(value: unknown): value is Resolution {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.wallId === "string" &&
    typeof entry.valueMm === "number" &&
    Number.isInteger(entry.valueMm) &&
    entry.valueMm >= MIN_LENGTH_MM &&
    entry.valueMm <= MAX_LENGTH_MM &&
    (entry.how === "getippt" || entry.how === "gewaehlt") &&
    (entry.origin === undefined || entry.origin === null || entry.origin === "ringfix")
  );
}

/**
 * What was kept for this reading — only well-formed entries, one per wall,
 * last one wins. The ring's suggestion keeps its `origin` across F5: taken
 * from the ring, it must not come back as a figure somebody read (§10.1).
 */
export function loadResolutions(caseId: string, extractionId: string): Resolution[] {
  const raw = storage()?.getItem(resolutionsKey(caseId, extractionId));
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const byWall = new Map<string, Resolution>();
    for (const entry of parsed) {
      if (!isResolution(entry)) continue;
      byWall.delete(entry.wallId);
      byWall.set(entry.wallId, {
        wallId: entry.wallId,
        valueMm: entry.valueMm,
        how: entry.how,
        ...(entry.origin === "ringfix" ? { origin: "ringfix" as const } : {}),
      });
    }
    return [...byWall.values()];
  } catch {
    return [];
  }
}

export function saveResolutions(caseId: string, extractionId: string, list: Resolution[]): void {
  const store = storage();
  if (!store) return;
  try {
    if (list.length === 0) store.removeItem(resolutionsKey(caseId, extractionId));
    else store.setItem(resolutionsKey(caseId, extractionId), JSON.stringify(list));
  } catch {
    // Full or refused: the screen keeps working, F5 forgets.
  }
}

export function clearResolutions(caseId: string, extractionId: string): void {
  saveResolutions(caseId, extractionId, []);
}
